'use client';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import type { PaymentsResponse } from '@//app/api/commerce/payments/[id]/list';
import type { ElasticVehicle, ElasticVehicleLeasingPromo } from '@//app/api/search/vehicles/[id]';
import { Tabs, type TabsSectionProps } from '@//components/Tabs';
import { WidgetBox } from '@//components/WidgetBox';
import usePayments, { type UsePaymentsVariables } from '@//data/ecom/usePayments';
import { ItemPageSection } from '../ItemPageSection';
import { BusinessLeasing } from './Tabs/BusinessLeasing';
import { Loan } from './Tabs/Loan';
import LoanSkeleton from './Tabs/Loan/LoanSkeleton';
import { PrivateLeasing } from './Tabs/PrivateLeasing';

interface FinanceSectionProps {
  id: string;
  itemId: string;
  branchId: string;
  paymentsResponse?: PaymentsResponse;
  businessLeasingPrice?: number | null;
  leasingPromos?: ElasticVehicleLeasingPromo[] | null;
  leasingPrice?: number | null;
  vehicle: ElasticVehicle;
}

export const FinanceSection: React.FC<FinanceSectionProps> = ({
  id,
  itemId,
  branchId,
  paymentsResponse,
  businessLeasingPrice,
  leasingPromos,
  leasingPrice,
  vehicle,
}) => {
  const t = useTranslations('pages.item.financeSection');

  const paymentsVariables = useMemo(
    (): UsePaymentsVariables => ({
      id: itemId,
      branchId,
    }),
    [itemId, branchId],
  );

  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const paymentsQuery = usePayments({
    variables: paymentsVariables,
    initialData: paymentsResponse,
  });
  const payments = paymentsQuery.data?.response;

  // Determine financing tab state
  const isLoading = !isHydrated || paymentsQuery.isLoading;
  const hasError = paymentsQuery.isError;
  const hasPaymentData = payments && payments.length > 0;

  // During hydration, always show loading to prevent mismatch
  // After hydration, conditionally show based on data availability
  const shouldShowFinancingTab = isLoading || (!hasError && hasPaymentData);

  const tabs = useMemo(() => {
    const _tabs: TabsSectionProps[] = [];

    // Show Finansiering tab during loading or if has payment data
    if (shouldShowFinancingTab) {
      _tabs.push({
        label: t('loan.label'),
        children: isLoading ? (
          <LoanSkeleton />
        ) : (
          <Loan payments={payments!} branchId={branchId} id={itemId} vehicle={vehicle} />
        ),
      });
    }

    if (leasingPrice) {
      _tabs.push({
        label: t('privateLeasing.label'),
        children: <PrivateLeasing leasingPromos={leasingPromos} leasingPrice={leasingPrice} />,
      });
    }

    if (businessLeasingPrice) {
      _tabs.push({
        label: t('businessLeasing.label'),
        children: <BusinessLeasing price={businessLeasingPrice} />,
      });
    }

    return _tabs;
  }, [shouldShowFinancingTab, isLoading, payments, branchId, itemId, businessLeasingPrice]);

  if (!hasPaymentData && !businessLeasingPrice && !leasingPrice) {
    return null;
  }

  return (
    <ItemPageSection id={id} heading={t('heading')}>
      <WidgetBox>
        <Tabs id="tabs-finance-options" overflowPadding="var(--_box-padding-inline)" tabs={tabs} />
      </WidgetBox>
    </ItemPageSection>
  );
};
